import type { CameraState, Isoline, ScalarGrid, Size } from '../types';
import { createProjector } from '../geometry/projection';
import type { SurfaceMesh } from '../geometry/surfaceMesh';
import { toLatticeFrame } from '../data/horizontalAxes';

export type SurfaceOverlayStyle = {
  showWireframe: boolean;
  showDropLines: boolean;
  showIsolines: boolean;
  opacity: number;
};

export type SurfaceOverlayArgs = {
  context: CanvasRenderingContext2D;
  mesh: SurfaceMesh;
  grid: ScalarGrid;
  camera: CameraState;
  viewport: Size;
  isolines: Isoline[];
  style: SurfaceOverlayStyle;
  inkColor: string;
};

/**
 * The line work that sits over the surface fill.
 *
 * Separated from the fill because the two now live on different canvases: a WebGPU surface cannot
 * also carry strokes, since a canvas has one context for its lifetime. Drawing the lines against
 * the same projector the fill was built from is what keeps them registered with it.
 *
 * Drawn without depth testing, so every line shows through the surface. That is deliberate for the
 * wireframe - hiding the back half would make the mesh read as a solid rather than a structure -
 * and it is the reason drop lines and contours stay legible where a peak stands in front of them.
 */
/**
 * The group each vertex belongs to, and how many groups there are.
 *
 * Both passes below ask "is this the same point of the surface as that one". Index equality only
 * answers it while the mesh is welded, and `toFacetedMesh` unwelds on purpose - so this reads the
 * mapping the unwelding recorded rather than recovering it by comparing positions.
 *
 * Recovering it cost 26 ms per frame at the adaptive budget: comparing three floats cheaply means
 * building a string from them, and that is three strings per triangle side. Caching the fold
 * against the mesh hid it for an orbit but not for a relief drag, because `heightScale` rebuilds
 * the mesh without touching a single position - so the cache missed on every frame of the one
 * gesture that rebuilds meshes fastest, and paid full price for an answer that had not changed.
 */
const toVertexGroups = (
  mesh: SurfaceMesh
): { groupOf: (index: number) => number; groupCount: number } => {
  const { sharedVertices, vertexCount } = mesh;
  if (!sharedVertices) {
    return { groupOf: index => index, groupCount: vertexCount };
  }

  let highest = 0;
  for (let index = 0; index < vertexCount; index++) {
    if (sharedVertices[index] > highest) highest = sharedVertices[index];
  }

  return { groupOf: index => sharedVertices[index], groupCount: highest + 1 };
};

export const renderSurfaceOverlay = ({
  context,
  mesh,
  grid,
  camera,
  viewport,
  isolines,
  style,
  inkColor,
}: SurfaceOverlayArgs): void => {
  if (mesh.triangleCount === 0) return;

  const project = createProjector(camera, viewport);
  const projectVertex = (index: number) => {
    const at = index * 3;
    return project(
      mesh.positions[at],
      mesh.positions[at + 1],
      mesh.positions[at + 2]
    );
  };

  const groups =
    style.showDropLines || style.showWireframe ? toVertexGroups(mesh) : null;

  context.save();
  context.lineJoin = 'round';

  if (style.showDropLines && groups) {
    context.globalAlpha = 0.1 * style.opacity;
    context.strokeStyle = inkColor;
    context.lineWidth = 0.5;
    context.beginPath();
    /* Every fourth distinct position: on a refined mesh the vertices cluster on the crests, and a
       line from each would read as a solid curtain under the ridge rather than a set of plumb
       lines. Counted over positions rather than over indices, because faceting triples the vertex
       count without adding a point to the surface - striding the raw indices tripled the lines
       and drew them from triangle corners, which is the curtain, reached by flipping an unrelated
       shading switch. */
    /* One vertex per group, chosen and then walked in group-id order.

       Not in vertex order, which is what "every fourth vertex" used to mean: that order is the
       vertex array's welded and triangle-corner order faceted, and the two only coincide on a
       uniform mesh, where `vertexAt` happens to create each vertex the first time a triangle
       names it. On the adaptive mesh - the default - they do not, and toggling faceted moved the
       plumb lines onto a different quarter of the surface while drawing the same number of them.

       Built from the triangles rather than from the vertex array, so a vertex left in no triangle
       gets no line. Welded, those were drawn hanging under nothing. */
    const representative = new Int32Array(groups.groupCount).fill(-1);
    for (const vertex of mesh.indices) {
      const group = groups.groupOf(vertex);
      const current = representative[group];
      if (current === -1 || vertex < current) representative[group] = vertex;
    }

    let position = 0;
    for (let group = 0; group < groups.groupCount; group++) {
      const index = representative[group];
      if (index === -1) continue;
      if (position++ % 4 !== 0) continue;

      const at = index * 3;
      const top = projectVertex(index);
      const base = project(mesh.positions[at], mesh.positions[at + 1], 0);
      context.moveTo(top.screenX, top.screenY);
      context.lineTo(base.screenX, base.screenY);
    }
    context.stroke();
  }

  if (style.showWireframe && groups) {
    /* Each edge stroked once. Triangles share edges, and stroking twice at this alpha would make
       shared edges visibly darker than boundary ones - which reads as structure that is not there.

       Keyed on the canonical indices of the endpoints, so an edge is recognised by where it is
       rather than by which vertices happen to name it. Keyed on the raw indices the dedup could
       not fire at all once the mesh was unwelded, which is the mode with the most triangles. */
    const drawn = new Set<number>();
    context.globalAlpha = 0.16 * style.opacity;
    context.strokeStyle = inkColor;
    context.lineWidth = 0.5;
    context.beginPath();

    for (let triangle = 0; triangle < mesh.indices.length; triangle += 3) {
      const corners = [
        mesh.indices[triangle],
        mesh.indices[triangle + 1],
        mesh.indices[triangle + 2],
      ];

      for (let edge = 0; edge < 3; edge++) {
        const from = corners[edge];
        const to = corners[(edge + 1) % 3];
        const fromGroup = groups.groupOf(from);
        const toGroup = groups.groupOf(to);
        const [low, high] =
          fromGroup < toGroup ? [fromGroup, toGroup] : [toGroup, fromGroup];
        /* Both are below `groupCount`, so the pair packs into one integer well inside the exact
           range: 1.3e9 at the faceted budget against 9.0e15. */
        const key = low * groups.groupCount + high;
        if (drawn.has(key)) continue;
        drawn.add(key);

        const start = projectVertex(from);
        const end = projectVertex(to);
        context.moveTo(start.screenX, start.screenY);
        context.lineTo(end.screenX, end.screenY);
      }
    }
    context.stroke();
  }

  if (style.showIsolines && isolines.length > 0) {
    const [zMin, zMax] = grid.zRange;
    const zSpan = zMax - zMin || 1;
    const { toCubeX, toCubeY } = toLatticeFrame(grid);

    context.globalAlpha = 0.5 * style.opacity;
    context.strokeStyle = inkColor;
    context.lineWidth = 1;

    for (const { threshold, points } of isolines) {
      context.beginPath();
      points.forEach(([gridX, gridY], index) => {
        /* Isolines come back in grid coordinates, so they are normalised here rather than by the
           extractor - which keeps that step independent of how any renderer frames the plot. */
        const { screenX, screenY } = project(
          toCubeX(gridX),
          toCubeY(gridY),
          (threshold - zMin) / zSpan
        );
        if (index === 0) {
          context.moveTo(screenX, screenY);
          return;
        }
        context.lineTo(screenX, screenY);
      });
      context.stroke();
    }
  }

  context.restore();
};
